import React from 'react';

import {
  ActionIcon,
  Checkbox,
  Container,
  Group,
  SegmentedControl,
  Text,
  Tooltip,
  createStyles,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconLayoutSidebar } from '@tabler/icons-react';
import KiloButton from 'components/KiloButton/KiloButton';

import type { AthleteCalendarFilters } from './types';
import { ProgramCalendarView } from '../types';

const useStyles = createStyles(theme => ({
  wrapper: {
    width: '100%',
    fontFamily: `${theme.fontFamily} !important`,
    backgroundColor: theme.colors.gray[0],
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
}));

interface AthleteCalendarHeaderProps {
  view: ProgramCalendarView;
  onViewChange: (view: ProgramCalendarView) => void;
  periodLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  panelCollapsed: boolean;
  onTogglePanel: () => void;
  filters: AthleteCalendarFilters;
  onFiltersChange: (filters: AthleteCalendarFilters) => void;
}

const AthleteCalendarHeader = ({
  view,
  onViewChange,
  periodLabel,
  onPrev,
  onNext,
  onToday,
  panelCollapsed,
  onTogglePanel,
  filters,
  onFiltersChange,
}: AthleteCalendarHeaderProps): JSX.Element => {
  const { classes } = useStyles();

  return (
    <Container fluid className={classes.wrapper}>
      <Group grow noWrap spacing={0} align='center'>
        <Group spacing='md' noWrap>
          <Tooltip label={panelCollapsed ? 'Show athlete list' : 'Hide athlete list'} withArrow>
            <ActionIcon
              variant={panelCollapsed ? 'filled' : 'default'}
              color='kiloBlue'
              onClick={onTogglePanel}
              aria-label={panelCollapsed ? 'Show athlete list' : 'Hide athlete list'}
              data-cy='athletePanelToggle'
            >
              <IconLayoutSidebar size={18} />
            </ActionIcon>
          </Tooltip>
          <Checkbox
            size='xs'
            label='Classes'
            checked={filters.showClasses}
            onChange={event => onFiltersChange({ ...filters, showClasses: event.currentTarget.checked })}
            data-cy='athleteCalendarShowClasses'
          />
          <Checkbox
            size='xs'
            label='Appointments'
            checked={filters.showAppointments}
            onChange={event =>
              onFiltersChange({ ...filters, showAppointments: event.currentTarget.checked })
            }
            data-cy='athleteCalendarShowAppointments'
          />
        </Group>

        <Group position='center'>
          <SegmentedControl
            data-cy='athleteCalendarViewSwitcher'
            size='sm'
            value={view}
            onChange={(value: ProgramCalendarView) => onViewChange(value)}
            data={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'agenda' },
            ]}
          />
        </Group>

        <Group position='right' noWrap spacing='xs'>
          <KiloButton variant='subtle' color='kiloBlue' size='sm' compact onClick={onToday}>
            Today
          </KiloButton>
          <ActionIcon onClick={onPrev} variant='subtle' color='kiloBlue' aria-label='Previous period'>
            <IconChevronLeft size={18} />
          </ActionIcon>
          <Text size='sm' fw={700} color='kiloBlue.6' data-cy='athleteCalendarPeriodLabel'>
            {periodLabel}
          </Text>
          <ActionIcon onClick={onNext} variant='subtle' color='kiloBlue' aria-label='Next period'>
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Container>
  );
};

export default AthleteCalendarHeader;
